import { useEffect, useState, useCallback } from 'react';
import { supabase, handleDatabaseError } from '../lib/supabase';
import { RealtimeChannel } from '@supabase/supabase-js';

export interface Product {
  id: string;
  name: string;
  price: number;
  image: string;
  category: string;
  description?: string;
  rating?: string;
  created_at?: string;
  updated_at?: string;
}

export function useProducts() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<RealtimeChannel | null>(null);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) {
        throw fetchError;
      }

      if (data) {
        setProducts(data);
      }
    } catch (err) {
      const errorMessage = handleDatabaseError(err);
      setError(errorMessage);
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const setupRealtimeSubscription = useCallback(() => {
    if (subscription) {
      supabase.removeChannel(subscription);
    }

    const channel = supabase
      .channel('products_db_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'products',
        },
        async (payload) => {
          console.log('Realtime change received:', payload);
          await fetchProducts();
        }
      )
      .subscribe((status) => {
        console.log('Subscription status:', status);
        
        if (status === 'SUBSCRIBED') {
          console.log('Successfully subscribed to changes');
        }
      });

    setSubscription(channel);
  }, [fetchProducts]);

  useEffect(() => {
    fetchProducts();
    setupRealtimeSubscription();

    return () => {
      if (subscription) {
        supabase.removeChannel(subscription);
      }
    };
  }, [fetchProducts, setupRealtimeSubscription]);

  const refreshProducts = useCallback(async () => {
    await fetchProducts();
  }, [fetchProducts]);

  return { products, loading, error, refreshProducts };
}